import { Button, Form, Input, InputNumber, Modal, Select, message } from "antd";
import { useCreateAssetMutation, useUpdateAssetMutation } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useState } from "react";
import { infrastructureTypes } from "../../Infrastructures/Infrastructures.d";

export type AssetModalProps = {
  visible: boolean;
  onCancel: () => void;
  preselectedPositionId?: string | null;
  positions?: any[];
  refetch?: () => void;
  editingAsset?: any | null; // Asset to edit, null for create mode
};

const assetTypes = [
  ...infrastructureTypes,
  { label: "Fridge", value: "FRIDGE" },
  { label: "Freezer", value: "FREEZER" },
  { label: "Heater", value: "HEATER" },
  { label: "Lighting", value: "LIGHTING" },
  { label: "Fan", value: "FAN" },
  { label: "Boiler", value: "BOILER" },
  { label: "Water Heater", value: "WATER_HEATER" },
  { label: "Oven", value: "OVEN" },
  { label: "Dishwasher", value: "DISHWASHER" },
  { label: "Washing Machine", value: "WASHING_MACHINE" },
  { label: "Dryer", value: "DRYER" },
  { label: "Vending Machine", value: "VENDING_MACHINE" },
  { label: "Other", value: "OTHER" },
];

const AssetModal = ({
  visible,
  onCancel,
  preselectedPositionId,
  positions,
  refetch,
  editingAsset,
}: AssetModalProps) => {
  const [form] = Form.useForm();
  const [showOtherType, setShowOtherType] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const isEditMode = !!editingAsset;

  const [createAsset, { loading: creating }] = useCreateAssetMutation({
    onCompleted: async () => {
      message.success("Asset created successfully");
      form.resetFields();

      // Refetch the assets and positions data BEFORE closing the modal
      // This ensures the data is fresh when the modal closes
      // TODO: Consider using Apollo cache update instead of refetch for better performance
      setIsRefetching(true);
      try {
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        message.warning(
          "Asset created successfully, but failed to refresh the list. Please refresh the page to see the new asset."
        );
        // Don't block the modal closing even if refetch fails
      } finally {
        setIsRefetching(false);
      }

      // Close modal after refetch completes
      onCancel();
    },
    onError: (error) => {
      message.error(error.message || "Failed to create asset");
    },
  });

  const [updateAsset, { loading: updating }] = useUpdateAssetMutation({
    onCompleted: async () => {
      message.success("Asset updated successfully");
      form.resetFields();

      // Refetch the assets and positions data BEFORE closing the modal
      setIsRefetching(true);
      try {
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        message.warning(
          "Asset updated successfully, but failed to refresh the list. Please refresh the page to see the changes."
        );
        // Don't block the modal closing even if refetch fails
      } finally {
        setIsRefetching(false);
      }

      // Close modal after refetch completes
      onCancel();
    },
    onError: (error) => {
      message.error(error.message || "Failed to update asset");
    },
  });

  // Set initial values when modal opens
  useEffect(() => {
    if (visible) {
      if (isEditMode && editingAsset) {
        // Populate form with existing asset data, including new fields 'name' and 'numOfPhases'
        const assetType = assetTypes.find((type) => type.value === editingAsset.type);
        const isOtherType = !assetType && editingAsset.type;

        form.setFieldsValue({
          assetLocation: editingAsset.parentPositionId,
          assetName: editingAsset.name,
          numOfPhases: editingAsset.numOfPhases,
          assetType: isOtherType ? "OTHER" : editingAsset.type,
          otherAssetType: isOtherType ? editingAsset.type : undefined,
          brand: editingAsset.brand,
          description: editingAsset.description,
          powerRating: editingAsset.powerRating,
          modelNumber: editingAsset.modelNumber,
          serialNumber: editingAsset.serialNumber,
        });
        setShowOtherType(isOtherType);
      } else if (preselectedPositionId) {
        // Set preselected position for create mode
        form.setFieldsValue({ assetLocation: preselectedPositionId });
      }
    }
  }, [visible, isEditMode, editingAsset, preselectedPositionId, form]);

  // Filter positions to include keys, autosets, and other droppable positions
  // Exclude metering point positions as assets shouldn't be placed there
  const assetLocationOptions = React.useMemo(() => {
    if (!positions) return [];

    return positions
      .filter((pos: any) => {
        // Include keys, autosets, zones, and groups but exclude metering points and deleted positions
        return !pos.deletedAt && pos.positionType !== "metering_point";
      })
      .map((pos: any) => {
        let positionLabel = pos.positionType;
        if (pos.positionType === "key" && pos.positionFunction === "identifier") {
          positionLabel = "Key";
        } else if (pos.positionType === "zone") {
          positionLabel = "Zone";
        } else if (pos.positionFunction === "autoset") {
          positionLabel = "Autoset";
        }

        return {
          label: `${pos.positionName} (${positionLabel})`,
          value: pos.positionId,
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [positions]);

  const handleAssetTypeChange = (value: string) => {
    setShowOtherType(value === "OTHER");
    if (value !== "OTHER") {
      form.setFieldsValue({ otherAssetType: undefined });
    }
  };

  const handleFinish = (values: any) => {
    const assetType = values.assetType === "OTHER" ? values.otherAssetType : values.assetType;
    const assetInput = {
      parentPositionId: values.assetLocation,
      name: values.assetName,
      brand: values.brand,
      description: values.description,
      modelNumber: values.modelNumber,
      serialNumber: values.serialNumber,
      powerRating: values.powerRating,
      numOfPhases: values.numOfPhases,
      type: assetType,
      // Optionally, you can add addedByUser if you have user context
    };

    if (isEditMode) {
      updateAsset({ variables: { id: editingAsset.id, asset: assetInput } });
    } else {
      createAsset({ variables: { asset: assetInput } });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowOtherType(false);
    onCancel();
  };

  const isLoading = creating || updating || isRefetching;

  return (
    <Modal
      title={isEditMode ? "Edit Asset" : "Create Asset"}
      centered
      visible={visible}
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="assetLocation" label="Asset Location" rules={[{ required: true }]}>
          <Select
            options={assetLocationOptions}
            placeholder="Select location"
            showSearch
            optionFilterProp="label"
            disabled={!!preselectedPositionId}
          />
        </Form.Item>
        <Form.Item name="assetName" label="Asset Name" rules={[{ required: true }]}>
          <Input placeholder="Enter asset name" />
        </Form.Item>
        <Form.Item name="numOfPhases" label="Number of Phases" rules={[{ required: true }]}>
          <Select
            placeholder="Select number of phases"
            options={[
              { label: "1 Phase", value: 1 },
              { label: "3 Phase", value: 3 },
            ]}
          />
        </Form.Item>
        <Form.Item name="assetType" label="Asset Type" rules={[{ required: true }]}>
          <Select options={assetTypes} placeholder="Select type" onChange={handleAssetTypeChange} />
        </Form.Item>
        {showOtherType && (
          <Form.Item
            name="otherAssetType"
            label="Specify Asset Type"
            rules={[{ required: true, message: "Please specify the asset type" }]}
          >
            <Input placeholder="Enter asset type" />
          </Form.Item>
        )}
        <Form.Item name="brand" label="Brand" rules={[{ required: true }]}>
          <Input placeholder="Enter brand" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input placeholder="Enter description" />
        </Form.Item>
        <Form.Item name="powerRating" label="Power Rating (Watts)" rules={[{ required: true }]}>
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter power rating in watts"
            min={0}
            addonAfter={<span style={{ fontWeight: "bold", color: "#1890ff" }}>W</span>}
          />
        </Form.Item>
        <Form.Item name="modelNumber" label="Model Number">
          <Input placeholder="Enter model number" />
        </Form.Item>
        <Form.Item name="serialNumber" label="Serial Number">
          <Input placeholder="Enter serial number" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={isLoading} disabled={isLoading}>
            {(() => {
              if (creating) return "Creating Asset...";
              if (updating) return "Updating Asset...";
              if (isRefetching) return "Updating List...";
              if (isEditMode) return "Update Asset";
              return "Create Asset";
            })()}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AssetModal;

// Keep the old export for backward compatibility
export { AssetModal as CreateAssetModal };
